import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_TABS = 3;
const storageKey = (userId, kind) => `c30.activity_bar.${kind}.${userId || 'anon'}`;

const readStored = (userId, kind) => {
  try {
    const raw = localStorage.getItem(storageKey(userId, kind));
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
};
const writeStored = (userId, kind, value) => {
  try { localStorage.setItem(storageKey(userId, kind), JSON.stringify(value)); } catch (_) { /* ignore */ }
};

const ActivityBarContext = createContext({
  headerHeight: 120,
  setHeaderHeight: () => {},
  activeActivity: null,
  openActivities: [],
  recentActivities: [],
  openActivity: () => {},
  updateActiveSnapshot: () => {},
  discardActive: () => {},
  minimizeActive: () => {},
  logRecentView: () => {},
  reopenOpen: () => {},
  reopenRecent: () => {},
  removeOpen: () => {},
  barSuppressed: false,
  suppressBar: () => () => {},
});

/**
 * Global "Activity Bar" state — powers Modal UI v2's multitasking system.
 * Any modal that opts in (checks useUIPreview().modalUiV2) can:
 *   - register itself as the live `activeActivity` when a user starts working
 *     on something (openActivity), keep it fresh (updateActiveSnapshot), and
 *     clear it when the user genuinely finishes/cancels (discardActive).
 *   - log a quick "recently viewed" entry (logRecentView) the moment a
 *     read-only view opens (e.g. viewing a customer/quote/call/project).
 * This provider centrally watches route changes: if `activeActivity` is
 * still set when the URL changes (the user navigated away without
 * finishing), it's automatically minimized into `openActivities` (capped at
 * 5, oldest auto-dropped) instead of being lost. Both tab queues persist to
 * localStorage per-user so they survive a page refresh.
 */
export const ActivityBarProvider = ({ user, children }) => {
  const location = useLocation();
  const userId = user?.id;
  const [headerHeight, setHeaderHeight] = useState(120);
  const [activeActivity, setActiveActivity] = useState(null);
  const [openActivities, setOpenActivities] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const prevPathRef = useRef(location.pathname);
  const [suppressCount, setSuppressCount] = useState(0);

  // A modal that never registers an "activity" (e.g. Customer Management on its search screen) can still ask for the
  // Open / Recent bar to be hidden while it is open. Returns a function that undoes it — use it as a useEffect cleanup.
  const suppressBar = useCallback(() => {
    setSuppressCount(c => c + 1);
    let released = false;
    return () => {
      if (!released) {
        released = true;
        setSuppressCount(c => Math.max(0, c - 1));
      }
    };
  }, []);

  // Load persisted queues whenever the signed-in user changes.
  useEffect(() => {
    setOpenActivities(readStored(userId, 'open').slice(0, MAX_TABS));
    setRecentActivities(readStored(userId, 'recent').slice(0, MAX_TABS));
  }, [userId]);

  useEffect(() => { writeStored(userId, 'open', openActivities); }, [userId, openActivities]);
  useEffect(() => { writeStored(userId, 'recent', recentActivities); }, [userId, recentActivities]);

  const minimizeActive = useCallback(() => {
    setActiveActivity(current => {
      if (!current) return null;
      setOpenActivities(prev => {
        const withoutDup = prev.filter(a => a.id !== current.id);
        const next = [{ ...current, updatedAt: Date.now() }, ...withoutDup];
        return next.slice(0, MAX_TABS);
      });
      return null;
    });
  }, []);

  // Central auto-minimize on navigation — fires once per real pathname change.
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      minimizeActive();
    }
  }, [location.pathname, minimizeActive]);

  const openActivity = useCallback(({ type, activityId, title, subtitle, snapshot = {} }) => {
    const id = `${type}:${activityId}`;
    // If a DIFFERENT activity was already active (e.g. opening the "Add to
    // CRM" sub-modal from inside an in-progress Quote/Pro Forma), minimize it
    // into the Open tab first instead of silently discarding it.
    setActiveActivity(current => {
      if (current && current.id !== id) {
        setOpenActivities(prev => {
          const withoutDup = prev.filter(a => a.id !== current.id && a.id !== id);
          return [{ ...current, updatedAt: Date.now() }, ...withoutDup].slice(0, MAX_TABS);
        });
      }
      return { id, type, activityId, title, subtitle, snapshot, renderedByHost: false };
    });
    setOpenActivities(prev => prev.filter(a => a.id !== id));
  }, []);

  const updateActiveSnapshot = useCallback((snapshotPatch, meta = {}) => {
    setActiveActivity(current => {
      if (!current) return current;
      // Guard against a background activity's snapshot patch landing on a
      // DIFFERENT activity that became active in the meantime (e.g. a nested
      // sub-modal took over) — only apply when the activityId still matches.
      if (snapshotPatch?.activityId && current.activityId !== snapshotPatch.activityId) return current;
      return {
        ...current,
        title: meta.title ?? current.title,
        subtitle: meta.subtitle ?? current.subtitle,
        snapshot: { ...current.snapshot, ...snapshotPatch },
      };
    });
  }, []);

  const discardActive = useCallback(() => setActiveActivity(null), []);

  const logRecentView = useCallback(({ type, activityId, title, subtitle, snapshot = {} }) => {
    const id = `${type}:${activityId}`;
    setRecentActivities(prev => {
      const entry = { id, type, activityId, title, subtitle, snapshot, updatedAt: Date.now() };
      const withoutDup = prev.filter(a => a.id !== id);
      return [entry, ...withoutDup].slice(0, MAX_TABS);
    });
  }, []);

  const reopenOpen = useCallback((id) => {
    setOpenActivities(prev => {
      const found = prev.find(a => a.id === id);
      if (found) setActiveActivity({ ...found, renderedByHost: true });
      return prev.filter(a => a.id !== id);
    });
  }, []);

  const reopenRecent = useCallback((id) => {
    setRecentActivities(prev => {
      const found = prev.find(a => a.id === id);
      if (found) setActiveActivity({ ...found, renderedByHost: true });
      return prev;
    });
  }, []);

  const removeOpen = useCallback((id) => {
    setOpenActivities(prev => prev.filter(a => a.id !== id));
  }, []);

  const value = useMemo(() => ({
    headerHeight,
    setHeaderHeight,
    activeActivity,
    openActivities,
    recentActivities,
    openActivity,
    updateActiveSnapshot,
    discardActive,
    minimizeActive,
    logRecentView,
    reopenOpen,
    reopenRecent,
    removeOpen,
    barSuppressed: suppressCount > 0,
    suppressBar,
  }), [headerHeight, activeActivity, openActivities, recentActivities, openActivity, updateActiveSnapshot, discardActive, minimizeActive, logRecentView, reopenOpen, reopenRecent, removeOpen, suppressCount, suppressBar]);

  return <ActivityBarContext.Provider value={value}>{children}</ActivityBarContext.Provider>;
};

export const useActivityBar = () => useContext(ActivityBarContext);
export default ActivityBarContext;
